import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon, IconButton } from '@ds/core';
import { Alert } from '@ds/feedback';
import { Input } from '@ds/forms';
import { Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { emMinutos, faixaSobreposta, horasSemanais, minutosDoDia } from '@/data/jornada';
import type { DiaDeFuncionamento } from '@/data/tipos';

const NOMES = ['Domingo', 'Segunda-feira', 'Terça-feira', 'Quarta-feira', 'Quinta-feira', 'Sexta-feira', 'Sábado'];

const horas = (minutos: number) => `${(Math.round((minutos / 60) * 10) / 10).toLocaleString('pt-BR')} h`;

/**
 * A jornada da clínica: dia fechado é dia sem faixa.
 *
 * O interruptor do dia não é um campo gravado — ligar o dia cria a primeira
 * faixa, desligar apaga as dele. Um `ativo` guardado ao lado da lista
 * discordaria dela: dia "ativo" sem faixa nenhuma é clínica que abre em horário
 * nenhum.
 */
export function JornadaSemanal({ horarios, horasSemanais: totalGravado }: { horarios: DiaDeFuncionamento[]; horasSemanais: number }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [dias, setDias] = useState<DiaDeFuncionamento[]>(horarios);
    const [salvando, setSalvando] = useState(false);

    const sujo = JSON.stringify(dias) !== JSON.stringify(horarios);
    const total = horasSemanais(dias);

    const trocarDia = (diaSemana: number, faixas: DiaDeFuncionamento['faixas']) =>
        setDias(dias.map((d) => (d.diaSemana === diaSemana ? { ...d, faixas } : d)));

    const erroDoDia = (dia: DiaDeFuncionamento): string | undefined => {
        const invertida = dia.faixas.find((f) => f.inicio && f.fim && emMinutos(f.fim) <= emMinutos(f.inicio));
        if (invertida) return `${invertida.inicio}–${invertida.fim} termina antes de começar.`;
        const cruzada = faixaSobreposta(dia.faixas);
        return cruzada ? `${cruzada.inicio} começa antes de a faixa anterior terminar.` : undefined;
    };

    const erros = dias.map(erroDoDia).filter(Boolean) as string[];

    const salvar = async () => {
        setSalvando(true);
        try {
            await dados().salvarHorarios(dias);
            aviso({ title: 'Jornada salva', description: `${horas(total * 60)} por semana` });
            revalidator.revalidate();
        } catch (e) {
            aviso({
                title: 'Não foi possível salvar a jornada',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Card
            title="Horários de funcionamento"
            subtitle="É esta jornada que dá o denominador da taxa de ocupação nos relatórios."
            padding={20}
            actions={(
                <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Badge tone={total > 0 ? 'neutral' : 'warning'}>{horas(total * 60)} por semana</Badge>
                    <Button variant="ghost" disabled={!sujo || salvando} onClick={() => setDias(horarios)}>Descartar</Button>
                    <Button onClick={salvar} disabled={!sujo || salvando || erros.length > 0}>
                        {salvando ? 'Salvando…' : 'Salvar'}
                    </Button>
                </span>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {dias.map((dia) => {
                    const erro = erroDoDia(dia);
                    const aberto = dia.faixas.length > 0;
                    return (
                        <div
                            key={dia.diaSemana}
                            style={{
                                display: 'grid', gridTemplateColumns: 'minmax(0,190px) 1fr', gap: 14, alignItems: 'start',
                                padding: '10px 0', borderTop: dia.diaSemana ? '1px solid var(--border)' : 'none',
                            }}
                        >
                            <Switch
                                checked={aberto}
                                label={NOMES[dia.diaSemana]}
                                description={aberto ? horas(minutosDoDia(dias, dia.diaSemana)) : 'Fechado'}
                                onChange={(ligado) => trocarDia(dia.diaSemana, ligado ? [{ inicio: '08:00', fim: '18:00' }] : [])}
                            />

                            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                {dia.faixas.map((faixa, i) => (
                                    <div key={`${dia.diaSemana}-${i}`} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Input
                                            type="time" value={faixa.inicio} size="sm" style={{ width: 120 }}
                                            aria-label={`Início da ${i + 1}ª faixa de ${NOMES[dia.diaSemana]}`}
                                            onChange={(v) => trocarDia(dia.diaSemana, dia.faixas.map((f, j) => (i === j ? { ...f, inicio: v } : f)))}
                                        />
                                        <span aria-hidden style={{ color: 'var(--text-tertiary)' }}>até</span>
                                        <Input
                                            type="time" value={faixa.fim} size="sm" style={{ width: 120 }}
                                            aria-label={`Fim da ${i + 1}ª faixa de ${NOMES[dia.diaSemana]}`}
                                            onChange={(v) => trocarDia(dia.diaSemana, dia.faixas.map((f, j) => (i === j ? { ...f, fim: v } : f)))}
                                        />
                                        <IconButton
                                            icon={<Icon name="trash-2" size={15} />} variant="ghost" size="sm"
                                            label={`Remover a ${i + 1}ª faixa de ${NOMES[dia.diaSemana]}`}
                                            onClick={() => trocarDia(dia.diaSemana, dia.faixas.filter((_, j) => j !== i))}
                                        />
                                    </div>
                                ))}

                                {aberto && (
                                    <span>
                                        <Button
                                            size="sm" variant="secondary"
                                            iconLeft={<Icon name="plus" size={14} />}
                                            onClick={() => trocarDia(dia.diaSemana, [...dia.faixas, { inicio: '13:30', fim: '19:00' }])}
                                        >
                                            Acrescentar faixa
                                        </Button>
                                    </span>
                                )}

                                {erro && (
                                    <span style={{ fontSize: 12.5, color: 'var(--danger)' }}>{erro}</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {total === 0 && (
                <Alert tone="warning" title="Sem jornada, não há taxa de ocupação" style={{ marginTop: 12 }}>
                    Os relatórios voltam a comparar os dias entre si enquanto a clínica não declarar em que
                    horários atende.
                </Alert>
            )}

            {/* O total gravado é o que os relatórios estão usando agora; o da
                tela é o que passará a valer. Mostrar os dois evita a leitura de
                que o relatório já mudou antes de alguém salvar. */}
            {sujo && (
                <p style={{ margin: '12px 0 0', fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    Os relatórios ainda usam {horas(totalGravado * 60)} por semana — o que está em uso é o
                    último salvo.
                </p>
            )}
        </Card>
    );
}
